import type { OperationsSnapshot } from "../domain/operations";

export const demoOperations: OperationsSnapshot = {
  events: [
    {
      id: "silveira",
      name: "Silveira Wedding",
      type: "Wedding",
      status: "confirmed",
      color: "blue",
      client: "Ana & Luis Silveira",
      dateLabel: "Today · 4:30 PM",
      dateRangeLabel: "Friday, September 12",
      location: "Hartwell Estate",
      headcount: 148,
      selectedService: "Full service dinner",
      leadStaffId: "harold",
      contacts: [
        { id: "ana", name: "Ana Silveira", role: "Client", phone: "707-555-0148", external: true },
        { id: "planner", name: "Maya Chen", role: "Planner", phone: "415-555-0192", external: true },
        { id: "harold", name: "Harold", role: "FSWL Lead", phone: "707-555-0101", external: false },
      ],
      keyTimes: [
        { id: "load", label: "Load", timeLabel: "10:45 AM" },
        { id: "arrive", label: "Arrive / setup", timeLabel: "1:00 PM" },
        { id: "guest", label: "Guest arrival", timeLabel: "4:00 PM" },
        { id: "service", label: "Dinner service", timeLabel: "4:30 PM" },
        { id: "end", label: "Service end", timeLabel: "9:30 PM" },
      ],
      flow: [
        { id: "f1", label: "Kitchen final prep", timeLabel: "7:00–10:30 AM", detail: "Broadway Kitchen stages protein, sauce, and beverage service." },
        { id: "f2", label: "Load / transport", timeLabel: "10:45 AM", detail: "Hot boxes and service equipment move Broadway → Hartwell." },
        { id: "f3", label: "Venue setup", timeLabel: "1:00 PM", detail: "Walkthrough, stations, and guest-facing service setup." },
        { id: "f4", label: "Dinner service", timeLabel: "4:30 PM", detail: "Plated dinner for 148 at Hartwell Estate." },
      ],
      commitments: [
        { id: "c1", title: "Plated dinner for 148", detail: "Four-course dinner with beverage service." },
        { id: "c2", title: "On-site service team", detail: "Lead plus three service staff through end of dinner." },
      ],
      requirements: [
        { id: "r1", title: "Produce delivery confirmed", detail: "Citrus and herbs for beurre blanc.", critical: true, resolved: false },
        { id: "r2", title: "Hartwell loading access", detail: "Service entrance reserved for 12:45 PM.", critical: true, resolved: true },
        { id: "r3", title: "Rental china count", detail: "Final pickup sheet signed.", critical: false, resolved: true },
      ],
      conditions: [
        { id: "cd1", title: "Produce delay", detail: "Citrus and herbs now expected 9:15 AM.", severity: "blocking" },
        { id: "cd2", title: "Clear weather window", detail: "No weather risk. Outdoor load path is dry.", severity: "info" },
      ],
    },
    {
      id: "kendall",
      name: "Kendall Baby Shower",
      type: "Private social",
      status: "confirmed",
      color: "plum",
      client: "Elena Kendall",
      dateLabel: "Sun · 11:00 AM",
      dateRangeLabel: "Sunday, September 14",
      location: "The Glass House",
      headcount: 48,
      selectedService: "Grazing and beverage",
      leadStaffId: "rosa",
      contacts: [
        { id: "elena", name: "Elena Kendall", role: "Client", phone: "510-555-0177", external: true },
        { id: "rosa", name: "Rosa", role: "FSWL Lead", phone: "707-555-0102", external: false },
      ],
      keyTimes: [
        { id: "k-load", label: "Load", timeLabel: "Sun 8:30 AM" },
        { id: "k-arrive", label: "Arrive", timeLabel: "Sun 9:30 AM" },
        { id: "k-service", label: "Guest arrival", timeLabel: "11:00 AM" },
      ],
      flow: [
        { id: "kf1", label: "Advance prep", timeLabel: "Today AM", detail: "Grazing components and labeled cold holding." },
        { id: "kf2", label: "Service", timeLabel: "Sunday 11:00 AM", detail: "Set grazing and beverage at The Glass House." },
      ],
      commitments: [
        { id: "kc1", title: "Grazing boards for 48", detail: "Guest count increased from 42 to 48 — quantities still under review." },
      ],
      requirements: [
        { id: "kr1", title: "Updated quantity sheet", detail: "Recost grazing for 48 guests.", critical: true, resolved: false },
        { id: "kr2", title: "Venue confirmation", detail: "Glass House arrival window.", critical: false, resolved: true },
      ],
      conditions: [
        { id: "kcd1", title: "Guest count change", detail: "Headcount is 48 until the client locks the list.", severity: "caution" },
      ],
    },
    {
      id: "chen",
      name: "Chen Corporate Dinner",
      type: "Corporate",
      status: "completed",
      color: "amber",
      client: "Chen Analytics",
      dateLabel: "Sep 10",
      dateRangeLabel: "Wednesday, September 10",
      location: "Riverfront Loft",
      headcount: 64,
      selectedService: "Passed + seated dinner",
      leadStaffId: "alex",
      contacts: [
        { id: "priya", name: "Priya Chen", role: "Client", phone: "650-555-0110", external: true },
        { id: "alex", name: "Alex Morgan", role: "FSWL Lead", phone: "707-555-0103", external: false },
      ],
      keyTimes: [
        { id: "ch-service", label: "Dinner service", timeLabel: "Wed 6:30 PM" },
        { id: "ch-end", label: "Strike", timeLabel: "Wed 10:00 PM" },
      ],
      flow: [
        { id: "cf1", label: "Service complete", timeLabel: "Sep 10", detail: "Dinner executed. Reset remains in Operations." },
      ],
      commitments: [
        { id: "cc1", title: "Seated dinner for 64", detail: "Delivered as booked." },
      ],
      requirements: [
        { id: "cr1", title: "Rental return", detail: "Coupe glass count must be reconciled before return.", critical: true, resolved: false },
      ],
      conditions: [
        { id: "ccd1", title: "Outstanding Reset", detail: "Event is Completed. Reset is still operational work.", severity: "caution" },
      ],
    },
    {
      id: "park",
      name: "Park Anniversary",
      type: "Private dinner",
      status: "cancelled",
      color: "plum",
      client: "Jordan Park",
      dateLabel: "Sep 20",
      dateRangeLabel: "Saturday, September 20",
      location: "Hillside Pavilion",
      headcount: 36,
      selectedService: "Family-style dinner",
      leadStaffId: "jamie",
      contacts: [
        { id: "jordan", name: "Jordan Park", role: "Client", phone: "925-555-0133", external: true },
        { id: "jamie", name: "Jamie", role: "FSWL Lead", phone: "707-555-0104", external: false },
      ],
      keyTimes: [
        { id: "p-service", label: "Dinner service", timeLabel: "6:00 PM" },
      ],
      flow: [
        { id: "pf1", label: "Cancelled", timeLabel: "Sep 8", detail: "Client cancelled. History retained. Future work is not active Operations." },
      ],
      commitments: [
        { id: "pc1", title: "Family-style dinner for 36", detail: "Cancelled before confirmation of final menu." },
      ],
      requirements: [
        { id: "pr1", title: "Deposit recorded", detail: "Cancellation logged against the inquiry.", critical: false, resolved: true },
      ],
      conditions: [
        { id: "pcd1", title: "Do not execute remaining prep", detail: "Unfinished work stays on the Event record only.", severity: "info" },
      ],
    },
  ],
  staff: [
    { id: "harold", name: "Harold", initials: "HA" },
    { id: "jamie", name: "Jamie", initials: "JM" },
    { id: "rosa", name: "Rosa", initials: "RL" },
    { id: "alex", name: "Alex", initials: "AM" },
  ],
  tasks: [
    { id: "stage-bins", eventId: "silveira", blockId: "kitchen-am", title: "Stage beverage service bins", detail: "Dry storage · Bay 2", classification: "final_prep", status: "complete", scheduledTime: "7:15 AM", assignedTo: "jamie" },
    { id: "chicken", eventId: "silveira", blockId: "kitchen-am", title: "Prep chicken roulade", detail: "Hot line · Station 1", classification: "final_prep", status: "in_progress", scheduledTime: "8:00 AM", assignedTo: "harold" },
    { id: "dessert-pull", eventId: "silveira", blockId: "kitchen-am", title: "Confirm dessert garnish pull", detail: "Pastry hold · Walk-in", classification: "advance", status: "eligible", assignedTo: "alex" },
    { id: "grazing", eventId: "kendall", blockId: "kitchen-am", title: "Assemble grazing components", detail: "Cold prep · Table 3", classification: "advance", status: "scheduled", scheduledTime: "9:00 AM", assignedTo: "rosa" },
    { id: "sauce", eventId: "silveira", blockId: "kitchen-am", title: "Finish citrus beurre blanc", detail: "Sauce station", classification: "final_prep", status: "blocked", scheduledTime: "8:30 AM", assignedTo: "jamie", blockerNote: "Waiting on produce delivery" },
    { id: "pull-linens", eventId: "silveira", blockId: "kitchen-am", title: "Pull service linens", detail: "Dry storage · Rack C", classification: "final_prep", status: "complete", assignedTo: "alex" },
    { id: "label-bins", eventId: "kendall", blockId: "kitchen-am", title: "Label cold holding bins", detail: "Cold prep", classification: "advance", status: "complete", assignedTo: "rosa" },
    { id: "load-hotboxes", eventId: "silveira", blockId: "load", title: "Load hot boxes and service equipment", detail: "Broadway → Hartwell Estate", classification: "final_prep", status: "scheduled", scheduledTime: "10:45 AM", assignedTo: "harold", activityLabel: "Load" },
    { id: "load-grazing", eventId: "kendall", blockId: "load", title: "Load grazing boards", detail: "Broadway loading bay", classification: "advance", status: "eligible", assignedTo: "rosa", activityLabel: "Load" },
    { id: "venue-walk", eventId: "silveira", blockId: "setup", title: "Lead venue walkthrough", detail: "Hartwell Estate · Service entry", classification: "on_site", status: "scheduled", scheduledTime: "1:00 PM", assignedTo: "harold" },
    { id: "reset-linens", eventId: "chen", blockId: "reset", title: "Return extra service linens", detail: "Broadway laundry staging", classification: "reset", status: "complete", assignedTo: "alex" },
    { id: "reset-glass", eventId: "chen", blockId: "reset", title: "Reconcile coupe glass count", detail: "Rental return staging", classification: "reset", status: "blocked", blockerNote: "12 glasses unaccounted for", assignedTo: "alex" },
    { id: "reset-pans", eventId: "chen", blockId: "reset", title: "Wash hotel pans", detail: "Broadway dish pit", classification: "reset", status: "eligible", assignedTo: "jamie" },
    { id: "park-menu", eventId: "park", blockId: "kitchen-am", title: "Draft family-style menu card", detail: "Would have been advance work for Sep 20", classification: "advance", status: "eligible", assignedTo: "jamie" },
    { id: "park-beverages", eventId: "park", blockId: "kitchen-am", title: "Confirm beverage pairing", detail: "Completed before cancellation", classification: "advance", status: "complete", assignedTo: "jamie" },
  ],
  blocks: [
    { id: "kitchen-am", label: "NOW", time: "7:00", meridiem: "AM", title: "Broadway Kitchen — Morning", timeframe: "7:00–10:30 AM", location: "Broadway Kitchen", icon: "kitchen", taskIds: ["stage-bins", "chicken", "dessert-pull", "grazing", "sauce", "pull-linens", "label-bins", "park-menu", "park-beverages"], staffIds: ["harold", "jamie", "rosa", "alex"] },
    { id: "load", label: "NEXT", time: "10:45", meridiem: "AM", title: "Load / Transport", timeframe: "10:45 AM–12:15 PM", location: "Broadway → Hartwell Estate", icon: "transport", taskIds: ["load-hotboxes", "load-grazing"], staffIds: ["harold", "jamie", "rosa"] },
    { id: "setup", label: "LATER", time: "1:00", meridiem: "PM", title: "Hartwell Estate — Setup", timeframe: "1:00–4:15 PM", location: "120 Hartwell Lane", icon: "venue", taskIds: ["venue-walk"], staffIds: ["harold", "jamie", "rosa", "alex"] },
    { id: "reset", label: "FLEX", time: "—", meridiem: "TODAY", title: "Kitchen Reset — As Capacity Allows", timeframe: "Outstanding Reset", location: "Broadway Kitchen", icon: "reset", taskIds: ["reset-linens", "reset-glass", "reset-pans"], staffIds: ["alex", "jamie"] },
  ],
  attention: [
    { id: "produce-delay", eventId: "silveira", kind: "blocker", title: "Produce delivery delayed", detail: "Citrus and herbs for Silveira are now expected at 9:15 AM.", timeLabel: "18 min ago", resolved: false },
    { id: "guest-count", eventId: "kendall", kind: "change", title: "Guest count updated", detail: "Kendall increased from 42 to 48 guests. Prep quantities need review.", timeLabel: "7:24 AM", resolved: false },
    { id: "rental-count", eventId: "chen", kind: "overdue", title: "Rental count not confirmed", detail: "12 coupe glasses remain unaccounted for after reset.", timeLabel: "Yesterday", resolved: false },
    { id: "park-cancel", eventId: "park", kind: "change", title: "Park Anniversary cancelled", detail: "Client cancelled. Remaining prep must not enter Today.", timeLabel: "Sep 8", resolved: false },
  ],
};
